import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Truck, Loader2, RefreshCw, ExternalLink, ShieldCheck, ClipboardPaste } from 'lucide-react';

const API = '/api';

export default function DistributorRecoveryPage() {
  const [suspects, setSuspects] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [promoting, setPromoting] = useState(false);
  const [pasteNames, setPasteNames] = useState('');
  const [pasteBusy, setPasteBusy] = useState(false);
  const [pasteResult, setPasteResult] = useState(null);

  const loadSuspects = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/distributor-suspects`, { withCredentials: true });
      setSuspects(res.data?.suspects || []);
      setSelected(new Set());
    } catch (e) {
      toast.error('Failed to scan: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSuspects();
  }, []);

  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(suspects.map(s => s.id)));
  const clearAll = () => setSelected(new Set());

  const handlePromote = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Re-promote ${selected.size} compan${selected.size === 1 ? 'y' : 'ies'} to Distributors? They'll move off the Pharma list onto the Distributors list.`)) return;
    setPromoting(true);
    try {
      const res = await axios.post(`${API}/admin/distributor-suspects/promote`,
        { profile_ids: Array.from(selected) },
        { withCredentials: true });
      toast.success(`Promoted ${res.data?.promoted || 0} → Distributors`);
      await loadSuspects();
    } catch (e) {
      toast.error('Promotion failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setPromoting(false);
    }
  };

  const handlePromoteByName = async () => {
    const names = pasteNames.split(/\r?\n|,/).map(s => s.trim()).filter(Boolean);
    if (names.length === 0) {
      toast.error('Paste at least one company name (one per line)');
      return;
    }
    setPasteBusy(true);
    setPasteResult(null);
    try {
      const res = await axios.post(`${API}/admin/distributor-suspects/promote-by-name`,
        { names },
        { withCredentials: true });
      setPasteResult(res.data);
      toast.success(`Promoted ${res.data?.promoted || 0} of ${names.length}`);
      await loadSuspects();
    } catch (e) {
      toast.error('Promote-by-name failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setPasteBusy(false);
    }
  };

  const scoreBadge = (s) => {
    if (s >= 99) return <Badge className="bg-emerald-500 text-white text-[10px]">User-known</Badge>;
    if (s >= 4) return <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Strong</Badge>;
    if (s >= 2) return <Badge className="bg-amber-100 text-amber-700 text-[10px]">Likely</Badge>;
    return <Badge className="bg-slate-100 text-slate-600 text-[10px]">Maybe</Badge>;
  };

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto" data-testid="distributor-recovery-page">
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Truck className="w-6 h-6 text-sky-700" />
            <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Recover Distributors
            </h1>
          </div>
          <p className="text-sm text-slate-500 max-w-2xl">
            Finds pharma profiles that look like distributors (regional in-licensers, trading houses, commercialization partners) and lets you re-promote them in bulk. Useful after a silent demotion regression.
          </p>
        </div>
        <Button onClick={loadSuspects} disabled={loading} variant="outline" size="sm" data-testid="rescan-btn">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
          Re-scan
        </Button>
      </div>

      <Card className="p-4 mb-4 bg-amber-50/40 border-amber-200">
        <div className="flex items-start gap-3 mb-2">
          <ClipboardPaste className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-800">Promote by name (fast path)</p>
            <p className="text-[11px] text-slate-600 mt-0.5">
              Paste names of companies you know are distributors (one per line, or comma-separated). The matcher is case-insensitive and falls back to "starts with" — so "Medis" matches "Medison Pharma" if no exact hit exists. Useful when the regex scan returns 0 but you remember the names.
            </p>
          </div>
        </div>
        <textarea
          value={pasteNames}
          onChange={(e) => setPasteNames(e.target.value)}
          rows={4}
          placeholder={"e.g.\nMedis\nArcera Life Sciences\nEr-Kim Pharmaceuticals\nEMS Pharma\nGrupo Insud"}
          className="w-full text-xs font-mono p-2 border border-slate-200 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-amber-300"
          data-testid="promote-by-name-textarea"
        />
        <div className="flex items-center justify-between mt-2">
          <span className="text-[11px] text-slate-500">
            {pasteNames.trim() ? `${pasteNames.split(/\r?\n|,/).map(s => s.trim()).filter(Boolean).length} name(s) ready` : 'No names yet'}
          </span>
          <Button onClick={handlePromoteByName} disabled={pasteBusy || !pasteNames.trim()} size="sm" className="bg-amber-600 hover:bg-amber-700" data-testid="promote-by-name-btn">
            {pasteBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}
            Promote pasted names → Distributors
          </Button>
        </div>
        {pasteResult && (
          <div className="mt-3 text-[11px] space-y-1.5" data-testid="paste-result">
            {pasteResult.promoted_names?.length > 0 && (
              <div className="bg-emerald-50 border border-emerald-200 rounded p-2">
                <p className="font-semibold text-emerald-800">✅ Promoted ({pasteResult.promoted}):</p>
                <p className="text-emerald-700 mt-0.5">{pasteResult.promoted_names.join(', ')}</p>
              </div>
            )}
            {pasteResult.ambiguous?.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded p-2">
                <p className="font-semibold text-amber-800">⚠️ Ambiguous (multiple matches — refine the name):</p>
                <ul className="text-amber-700 mt-0.5 space-y-0.5">
                  {pasteResult.ambiguous.map((a, i) => (
                    <li key={i}><strong>{a.name}</strong> → {a.matches.join(', ')}</li>
                  ))}
                </ul>
              </div>
            )}
            {pasteResult.not_found?.length > 0 && (
              <div className="bg-rose-50 border border-rose-200 rounded p-2">
                <p className="font-semibold text-rose-800">❌ Not found in CRM ({pasteResult.not_found.length}):</p>
                <p className="text-rose-700 mt-0.5">{pasteResult.not_found.join(', ')}</p>
                <p className="text-[10px] text-rose-600 mt-1 italic">These names don't exist as company_profiles. Either spelled differently in your DB, or they were hard-deleted (use Atlas restore if needed).</p>
              </div>
            )}
          </div>
        )}
      </Card>

      <Card className="p-4 mb-4 bg-sky-50/40 border-sky-200">
        <div className="flex items-center gap-3 text-xs text-slate-700">
          <ShieldCheck className="w-4 h-4 text-sky-600 shrink-0" />
          <div>
            <p><strong>Heuristic scan below:</strong> Flags companies with distributor signals in name/type/notes. Use this for unknown demotions. <strong>Review carefully</strong> — a name like "Carnot Pharma" can be a real pharma.</p>
          </div>
        </div>
      </Card>

      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-slate-600">
          {loading ? 'Scanning…' : `${suspects.length} suspect${suspects.length === 1 ? '' : 's'} found · ${selected.size} selected`}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={selectAll} disabled={suspects.length === 0} className="text-xs text-sky-600 hover:underline disabled:text-slate-300" data-testid="select-all-btn">Select all</button>
          <span className="text-slate-300">·</span>
          <button onClick={clearAll} disabled={selected.size === 0} className="text-xs text-slate-500 hover:underline disabled:text-slate-300" data-testid="clear-all-btn">Clear</button>
          <Button onClick={handlePromote} disabled={selected.size === 0 || promoting} size="sm" className="ml-3 bg-emerald-600 hover:bg-emerald-700" data-testid="promote-btn">
            {promoting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}
            Re-promote {selected.size > 0 ? `(${selected.size})` : ''} → Distributors
          </Button>
        </div>
      </div>

      {loading ? (
        <Card className="p-12 text-center">
          <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400 mb-2" />
          <p className="text-sm text-slate-500">Scanning company profiles…</p>
        </Card>
      ) : suspects.length === 0 ? (
        <Card className="p-12 text-center">
          <ShieldCheck className="w-8 h-8 mx-auto text-emerald-500 mb-2" />
          <p className="text-sm font-medium text-slate-700">No suspect distributors found</p>
          <p className="text-xs text-slate-500 mt-1">Every distributor-looking company is already correctly classified.</p>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="grid grid-cols-[40px_2fr_1fr_2fr_120px] px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
            <div />
            <div>Company</div>
            <div>Type</div>
            <div>Match Reasons</div>
            <div className="text-center">Confidence</div>
          </div>
          <div className="divide-y divide-slate-100">
            {suspects.map(s => (
              <div key={s.id} className="grid grid-cols-[40px_2fr_1fr_2fr_120px] px-4 py-3 items-start hover:bg-slate-50/60" data-testid={`suspect-row-${s.id}`}>
                <div className="pt-0.5">
                  <Checkbox checked={selected.has(s.id)} onCheckedChange={() => toggle(s.id)} data-testid={`suspect-checkbox-${s.id}`} />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm font-medium text-slate-900 truncate">{s.company_name}</p>
                    <a href={`/company-profile/${s.id}`} target="_blank" rel="noopener noreferrer" className="text-sky-500 hover:text-sky-700 shrink-0" data-testid={`suspect-open-${s.id}`}>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                  {s.notes_preview && <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">{s.notes_preview}</p>}
                </div>
                <div className="text-xs text-slate-600 truncate">{s.company_type || '—'}</div>
                <div className="flex flex-wrap gap-1">
                  {(s.match_reasons || []).map((r, i) => (
                    <span key={i} className="text-[10px] bg-slate-100 text-slate-600 rounded px-1.5 py-0.5">{r}</span>
                  ))}
                </div>
                <div className="flex justify-center pt-0.5">{scoreBadge(s.score)}</div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
